<template>
  <div class="p-6">

    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-1">
        Roles y Secciones
      </h1>
      <p class="text-gray-500 dark:text-gray-400 text-sm">
        Asigna qué secciones de producción puede ver cada rol en el KDS (Mi Monitor).
      </p>
    </div>

    <!-- Error global -->
    <div v-if="errorGlobal" class="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg px-4 py-3 text-sm">
      {{ errorGlobal }}
    </div>

    <!-- Loading inicial -->
    <div v-if="loadingInicial" class="flex items-center justify-center py-20 text-gray-400">
      <svg class="animate-spin h-6 w-6 mr-3" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
      </svg>
      Cargando...
    </div>

    <div v-else class="grid grid-cols-1 lg:grid-cols-3 gap-6">

      <!-- ─── Columna izquierda: Lista de roles ─── -->
      <div class="lg:col-span-1">
        <div class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
          <div class="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <h2 class="text-sm font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide">Roles</h2>
          </div>
          <ul class="divide-y divide-gray-100 dark:divide-gray-700">
            <li
              v-for="rol in roles"
              :key="rol.id"
              @click="seleccionarRol(rol)"
              class="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors"
              :class="rolSeleccionado?.id === rol.id
                ? 'bg-violet-50 dark:bg-violet-900/20 border-l-2 border-violet-500'
                : 'hover:bg-gray-50 dark:hover:bg-gray-700'"
            >
              <span class="text-xl">{{ iconRol(rol.id) }}</span>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">{{ rol.nombre }}</div>
                <div class="text-xs text-gray-400 dark:text-gray-500">
                  {{ seccionesPorRol[rol.id]?.length ?? 0 }} sección(es)
                </div>
              </div>
              <svg class="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </li>
          </ul>
          <div v-if="roles.length === 0" class="py-8 text-center text-gray-400 text-sm">
            No hay roles disponibles
          </div>
        </div>
      </div>

      <!-- ─── Columna derecha: Asignación de secciones ─── -->
      <div class="lg:col-span-2">

        <!-- Sin rol seleccionado -->
        <div v-if="!rolSeleccionado" class="flex flex-col items-center justify-center h-full min-h-[300px] text-gray-400 dark:text-gray-600 gap-3">
          <svg class="w-14 h-14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5"/>
          </svg>
          <p class="text-sm">Selecciona un rol para asignar sus secciones</p>
        </div>

        <!-- Panel de asignación -->
        <div v-else class="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">

          <!-- Header del panel -->
          <div class="px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
            <div class="flex items-center gap-3">
              <span class="text-2xl">{{ iconRol(rolSeleccionado.id) }}</span>
              <div>
                <h3 class="text-base font-bold text-gray-900 dark:text-white">{{ rolSeleccionado.nombre }}</h3>
                <p class="text-xs text-gray-400">
                  {{ seccionesSeleccionadas.length }} de {{ secciones.length }} secciones asignadas
                </p>
              </div>
            </div>
            <div class="flex gap-2">
              <button
                class="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                @click="seleccionarTodas"
              >
                Todas
              </button>
              <button
                class="text-xs px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                @click="limpiarSeleccion"
              >
                Ninguna
              </button>
            </div>
          </div>

          <!-- Loading secciones del rol -->
          <div v-if="loadingSecciones" class="flex items-center justify-center py-10 text-gray-400">
            <svg class="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
            </svg>
            Cargando secciones...
          </div>

          <!-- Grid de checkboxes de secciones -->
          <div v-else class="p-5">
            <div v-if="secciones.length === 0" class="text-center text-gray-400 text-sm py-8">
              No hay secciones configuradas en el sistema.
            </div>
            <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label
                v-for="seccion in secciones"
                :key="seccion.seccionId"
                class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors select-none"
                :class="estaSeleccionada(seccion.seccionId)
                  ? 'border-violet-400 bg-violet-50 dark:bg-violet-900/20 dark:border-violet-600'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'"
              >
                <input
                  type="checkbox"
                  class="mt-0.5 h-4 w-4 text-violet-600 rounded border-gray-300 dark:border-gray-600 focus:ring-violet-500 cursor-pointer"
                  :checked="estaSeleccionada(seccion.seccionId)"
                  @change="toggleSeccion(seccion.seccionId)"
                />
                <div class="flex-1 min-w-0">
                  <div class="text-sm font-semibold text-gray-800 dark:text-gray-100">{{ seccion.nombreSeccion }}</div>
                  <div v-if="seccion.descripcion" class="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">
                    {{ seccion.descripcion }}
                  </div>
                  <div class="text-xs text-gray-300 dark:text-gray-600 mt-0.5">
                    Orden: {{ seccion.orden }}
                  </div>
                </div>
              </label>
            </div>
          </div>

          <!-- Footer con botón guardar -->
          <div class="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <!-- Toast de éxito -->
            <transition name="fade">
              <span v-if="guardadoOk" class="flex items-center gap-1.5 text-sm text-green-600 dark:text-green-400 font-medium">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
                Guardado correctamente
              </span>
              <span v-else class="text-xs text-gray-400">
                Los cambios no se guardan automáticamente
              </span>
            </transition>

            <button
              @click="guardar"
              :disabled="guardando"
              class="flex items-center gap-2 px-5 py-2 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors"
            >
              <svg v-if="guardando" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              <svg v-else class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              Guardar cambios
            </button>
          </div>

        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import axios from 'axios'

// ─── Instancia axios con token ────────────────────────────────────────────────
const api = axios.create({
  baseURL: import.meta.env.VITE_API_ENDPOINT || 'http://localhost:8000/api/',
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
})
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// ─── Estado ───────────────────────────────────────────────────────────────────
const roles               = ref([])
const secciones           = ref([])
const rolSeleccionado     = ref(null)
const seccionesSeleccionadas = ref([])   // array de seccionId
const seccionesPorRol     = reactive({}) // { rolId: [seccionId, ...] }

const loadingInicial  = ref(true)
const loadingSecciones= ref(false)
const guardando       = ref(false)
const guardadoOk      = ref(false)
const errorGlobal     = ref(null)

// ─── Carga inicial ────────────────────────────────────────────────────────────
onMounted(async () => {
  try {
    const [rolesRes, seccionesRes] = await Promise.all([
      api.get('roles'),
      api.get('secciones-new'),
    ])
    roles.value    = rolesRes.data
    secciones.value = seccionesRes.data?.data ?? seccionesRes.data ?? []

    // Pre-cargar secciones de cada rol (en paralelo)
    await Promise.all(
      roles.value.map(async (rol) => {
        const res = await api.get(`roles/${rol.id}/secciones`)
        const ids = (res.data.data ?? []).map(s => s.seccionId)
        seccionesPorRol[rol.id] = ids
      })
    )
  } catch (e) {
    errorGlobal.value = 'Error al cargar datos. Verifica la conexión con el servidor.'
    console.error(e)
  } finally {
    loadingInicial.value = false
  }
})

// ─── Selección de rol ─────────────────────────────────────────────────────────
async function seleccionarRol(rol) {
  guardadoOk.value = false
  rolSeleccionado.value = rol
  loadingSecciones.value = true
  try {
    const res = await api.get(`roles/${rol.id}/secciones`)
    const ids = (res.data.data ?? []).map(s => s.seccionId)
    seccionesSeleccionadas.value = ids
    seccionesPorRol[rol.id] = ids
  } catch (e) {
    console.error(e)
    seccionesSeleccionadas.value = seccionesPorRol[rol.id] ?? []
  } finally {
    loadingSecciones.value = false
  }
}

// ─── Helpers de sección ───────────────────────────────────────────────────────
function estaSeleccionada(seccionId) {
  return seccionesSeleccionadas.value.includes(seccionId)
}

function toggleSeccion(seccionId) {
  const idx = seccionesSeleccionadas.value.indexOf(seccionId)
  if (idx === -1) {
    seccionesSeleccionadas.value.push(seccionId)
  } else {
    seccionesSeleccionadas.value.splice(idx, 1)
  }
}

function seleccionarTodas() {
  seccionesSeleccionadas.value = secciones.value.map(s => s.seccionId)
}

function limpiarSeleccion() {
  seccionesSeleccionadas.value = []
}

// ─── Guardar ──────────────────────────────────────────────────────────────────
async function guardar() {
  if (!rolSeleccionado.value) return
  guardando.value = true
  guardadoOk.value = false
  try {
    await api.put(`roles/${rolSeleccionado.value.id}/secciones`, {
      seccion_ids: seccionesSeleccionadas.value,
    })
    // Actualizar caché local
    seccionesPorRol[rolSeleccionado.value.id] = [...seccionesSeleccionadas.value]
    guardadoOk.value = true
    setTimeout(() => { guardadoOk.value = false }, 3000)
  } catch (e) {
    errorGlobal.value = 'Error al guardar. Intenta de nuevo.'
    console.error(e)
    setTimeout(() => { errorGlobal.value = null }, 4000)
  } finally {
    guardando.value = false
  }
}

// ─── Helpers visuales ─────────────────────────────────────────────────────────
function iconRol(rolId) {
  const icons = { 1: '👑', 2: '🛠️', 3: '🧑‍💼', 4: '🧑‍💻', 5: '🧑‍🔧' }
  return icons[rolId] ?? '👤'
}
</script>

<style scoped>
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
